<template>
    <div class="row item " :class="{ isPublished, even: index % 2 === 0, isDefault }">
        <loader :loading="isLoading" v-if="isLoading"></loader>
        <div class="col-sm-1 d-flex align-items-center px-0 text-break name">
            <div
                v-if="!isDefault"
                v-b-tooltip.hover.right="statusName(forecastWindow.forecast.status).toUpperCase()"
                class="mr-3 align-content-center text-center"
                :style="getStatusBarStyle"
            >
                <span class="statusBadge">{{ getStatusLetter }}</span>
            </div>
            <span v-if="isDefault">Default</span><span v-else>{{ forecastWindow.zone.name }}</span>
            <span :class="{ hidden: !forecastWindow.multiValidation }" class="multiValidation"><MarketIcon /></span>
        </div>
        <div class="col-sm-2 align-content-center">
            <Datepicker
                :value="startDate"
                @input="forecastWindow.startDate = $event"
                :min="getDateByFormat(min, 'YYYY-MM-DD')"
                :max="forecastWindow.endDate ? getDateByFormat(forecastWindow.endDate, 'YYYY-MM-DD') : getDateByFormat(min, 'YYYY-MM-DD')"
            />
        </div>
        <div class="col-sm-2 align-content-center">
            <Datepicker
                :value="endDate"
                @input="forecastWindow.endDate = $event"
                :min="forecastWindow.startDate ? getDateByFormat(forecastWindow.startDate, 'YYYY-MM-DD') : getDateByFormat(min, 'YYYY-MM-DD')"
                :max="getDateByFormat(max, 'YYYY-MM-DD')"
            />
        </div>
        <div class="col-sm-1 align-content-center">
            <div class="days">
                <input type="number" min="0" v-model="forecastWindow.daysBeforeNotification" /> <span style="font-size: 0.7em">Days</span>
            </div>
        </div>
        <div class="col-sm-3 align-content-center comment" :class="{ even: index % 2 === 0 }">
            <textarea v-model="forecastWindow.supervisorComment" rows="4" />
        </div>
        <template v-if="isDefault">
            <div class="col-sm-1 align-content-center"></div>
            <div class="col-sm-2 align-content-center">
                <ClientColorButton @click="$emit('applyDefault')" style="width: 100%" :disabled="isApplyDisabled">
                    Apply default to selected
                </ClientColorButton>
            </div>
        </template>
        <template v-else>
            <div class="col-sm-1 align-content-center" style="text-align: center">
                <input type="checkbox" :checked="isSelected" @click="toggleSelect" />
            </div>
            <div class="col-sm-2 align-content-center">
                <ClientColorButton style="width: 100%" @click="saveForecastWindows" :size="'m'" :disabled="disableSaveButton">
                    {{ buttonText }}
                </ClientColorButton>
            </div>
        </template>
    </div>
</template>
<script>
import { getStatusColor, getStatusLetter as getStatusLetterUtils, getStatusName } from "@/components/forecasts/utils/utilis";
import Datepicker from "@/components/forecasts/elements/form/Datepicker";
import forecastsService from "@/components/forecasts/forecastsService";
import loader from "@/components/loader/loader";
import MarketIcon from "../icons/MarketIcon.vue";
import { convertForecastInForecastWindow } from "../../model/forecastWindow";

export default {
    components: { MarketIcon, Datepicker, loader },
    props: {
        forecastWindow: {
            type: Object,
            required: true
        },
        min: {
            type: Date,
            required: false
        },
        max: {
            type: Date,
            required: false
        },
        index: {
            type: Number,
            default: 1
        },
        isDefault: {
            type: Boolean,
            default: false
        },
        isSelected: {
            type: Boolean,
            default: false
        },
        isValid: {
            type: Boolean,
            required: false
        },
        isApplyDisabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isPublished: false,
            isLoading: false
        };
    },
    created() {
        this.forecastWindow = convertForecastInForecastWindow(this.forecastWindow);

        if (this.forecastWindow.publishedDate) {
            this.isPublished = true;
        }

        if (!this.forecastWindow.daysBeforeNotification) {
            this.forecastWindow.daysBeforeNotification = 5;
        }

        if (!this.forecastWindow.startDate && this.min) {
            this.forecastWindow.startDate = this.min;
        }

        if (!this.forecastWindow.endDate && this.max) {
            this.forecastWindow.endDate = this.max;
        }
    },
    watch: {
        min(newVal) {
            if (!this.forecastWindow.startDate && newVal) {
                this.forecastWindow.startDate = newVal;
            }
        },
        max(newVal) {
            if (!this.forecastWindow.endDate && newVal) {
                this.forecastWindow.endDate = newVal;
            }
        }
    },
    computed: {
        disableSaveButton() {
            return !this.isValid;
        },
        buttonText() {
            return this.isPublished ? "Modify and re-publish" : "Publish";
        },
        startDate() {
            return this.forecastWindow.startDate ? this.forecastWindow.startDate : this.min ? this.min : null;
        },
        endDate() {
            return this.forecastWindow.endDate ? this.forecastWindow.endDate : this.max ? this.max : null;
        },
        getStatusBarStyle() {
            if (this.forecastWindow.forecast && this.forecastWindow.forecast.status) {
                const color = this.statusColor(this.forecastWindow.forecast.status);
                return { backgroundColor: color, minWidth: "20px", width: "20px", height: "100%" };
            }
            return {};
        },
        getStatusLetter() {
            if (this.forecastWindow.forecast && this.forecastWindow.forecast.status) {
                return this.statusLetter(this.forecastWindow.forecast.status);
            }
            return "";
        }
    },
    methods: {
        async saveForecastWindows() {
            const text = this.isPublished
                ? "Do you really want to republish this secondary window?"
                : "Do you really want to publish this secondary window?";
            const buttonText = this.isPublished ? "Republish" : "Publish";
            const res = await this.dialogConfirm(text, buttonText, "Cancel");
            if (res === true) {
                this.isLoading = true;
                try {
                    await forecastsService.updateForecastSecondaryWindow(this.forecastWindow);
                    this.dialogSuccess("Successfully (re)published");
                    this.$emit("published");
                } catch (e) {
                    if (e.response && e.response.data) {
                        this.dialogError(e.response.data.join("\n"));
                    } else {
                        this.dialogError(e);
                    }
                } finally {
                    this.isLoading = false;
                }
            }
        },
        toggleSelect() {
            this.$emit("toggleSelect");
        },
        statusName(status) {
            return getStatusName(this.forecastWindow.forecast.deleted ? "DELETE" : status);
        },
        statusColor(status) {
            return getStatusColor(this.forecastWindow.forecast.deleted ? "DELETE" : status);
        },
        statusLetter(status) {
            return getStatusLetterUtils(this.forecastWindow.forecast.deleted ? "DELETE" : status);
        }
    }
};
</script>
<style scoped lang="scss">
@import "../../css/forecasts";
@import "../../../../assets/css/themes.scss";

.days {
    text-align: right;
    input {
        width: 40px;
    }
}
.comment {
    padding-top: 15px;
    padding-bottom: 15px;
    textarea {
        width: 100%;
    }
}
.item {
    &.even {
        background-color: #e9eae5;
    }
    &.isDefault {
        padding-left: 10px;
        background-color: #aaaaa5;
    }
}
.name {
    font-weight: bold;
}
.statusBadge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #000000;
    color: #ffffff;
    border-radius: 12px;
    height: 20px;
    min-width: 20px;
}
.multiValidation {
    height: 20px;
    min-width: 20px;
    width: 20px;
    display: inline-block;
    margin-left: 5px;
}
.hidden {
    visibility: hidden;
}
</style>



// WEBPACK FOOTER //
// SecondaryWindowForm.vue?e2b278e0